/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Language} from "types/entities/Language";
import {HallCode} from "types/enums/HallCode";
import {TrackDomain} from "types/enums/TrackDomain";
import {PackageCategory} from "types/enums/PackageCategory";

export const GOOGLE_MAPS_PUBLIC_KEY = "AIzaSyAfS1EvTnPRzShMAZjsreykidNDGxBijws";
// export const GOOGLE_ANALYTICS_KEY = "UA-60205697-4";

export const SELLER_API_KEY = "777f80c6-6d00-11ee-843d-88d7f67de6c2";
export const API_KEY = "777f80c6-6d00-11ee-843d-88d7f67de6c2";

export const PROTOCOL = "https";
export const IS_DEV = document.location.port?.includes("30") || false;
export const IS_REMOTE_DEV = document.location.hostname.includes("192.168") || document.location.hostname.includes("staging.arbopark.ch") || true;
export const IS_PROD = !IS_DEV && !IS_REMOTE_DEV;
export const TRACK_DOMAIN = TrackDomain.ArboPark;
export const ROOT_STORAGE_DOMAIN = IS_DEV ? "cl.arbopark.ch" : "cl.arbopark.ch";
export const ROOT_STORAGE_WEB = `${PROTOCOL}://${ROOT_STORAGE_DOMAIN}/`;
export const ROOT_API_WEB = `${PROTOCOL}://${ROOT_STORAGE_DOMAIN}`;
export const ROOT_API = ROOT_API_WEB + "/api/";
export const DEFAULT_HALL: HallCode = HallCode.ArboPark;
export const ACTIVITIES_SELECTION_ALLOWED: boolean = false;
export const GROUP_TYPES_ALLOWED: boolean = false;
export const SELECT_CUSTOM_TIMESLOTS_ALLOWED: boolean = false;
export const USE_POSSIBLE_TIMESLOTS_ALLOWED: boolean = false;
export const DRIVERS_NAMES_CHECKIN_ALLOWED: boolean = false;
export const SHOW_ITEM_SLOT_RANGE_ONLY: boolean = true;
export const SHOW_ONLY_RESERVATION_START: boolean = true;
export const FILTER_TIMESLOTS_PER_PERSON_TYPE: boolean = false;
export const SHOW_TYPICAL_TIMEPLAN: boolean = false;
export const SHOW_STANDARD_PRICE: boolean = false;
export const SHOW_BOOKABLE_DAYS_FROM_NOW: boolean = false;
export const ENABLE_HELP_WITH_TIMESLOTS: boolean = false;
export const ENABLED_COUNTDOWN_TIMER: boolean = false;
export const SHOW_NOTIFICATION_METHODS: boolean = false;
export const IS_VOUCHER_ALLOWED_IN_SHOPPING_CART: boolean = true;
export const FELIXTAG_VOUCHER_BANNER_ENABLED: boolean = false;

export const VOUCHERS_LIMIT: number = 10; //limit to max 100 vouchers

export const MIN_PERSON_BIRTHDATE_AGE = 8;

export const MIN_COUNT_FOR_INQUIRY = 100;

export const SELL_GIFTCARD_VOUCHERS = true;
export const SHOW_TIMESLOT_OCCUPANCY = false;
export const SHOW_IMPORTANT_ARRIVE_TIME = false;

export const DEFAULT_COUNTRY_CODE = "CH";
export const REQUIRE_FULL_ADDRESS_IN_REGISTRATION = true;
export const GUEST_PHONE_EMAIL_MANDATORY = true;
export const GUEST_ADDRESS_MANDATORY = true;

export const EMAIL_LOGIN_REQUIRED = true;
export const EMAIL_VERIFICATION_REQUIRED = false;
export const SHOW_PRICE_PER_PERSON_FOR_PACKAGE_CATEGORY_EVENT = true;
export const MAX_MONTH_COUNT_FOR_AVAILABLE_DATES = 7;
export const LOGIN_CODE_LENGTH = 4;

let HALL_CURRENCY;
let HALL_CURRENCY_FRACTION_DIGITS;
let HALL_LOCALE;
let HALL_TAX;
let HALL_DEFAULT_LANGUAGE;
let HALL_DEFAULT_HALL_ID;
let HALL_DEFAULT_RESERVATION_DAYS_LIMIT;
let HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW;
let HALL_OFFICIAL_NAME;
let HALL_RESERVATION_ARRIVAL_MINUTES;
let HALL_GOOGLE_ANALYTICS_KEY;
let HALL_GOOGLE_TAG_MANAGER_ID;
let HALL_DEFAULT_BASENAME;
let HALL_LANGUAGES;
let SHOW_PER_PERSON_PRICE;
let SHOW_READ_TERMS_BUTTON;
let ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT;
let HALL_IS_ONLY_BOOKING;
let HALL_HAS_BASENAME;

if ((DEFAULT_HALL as HallCode) === HallCode.Winterthur) {
    HALL_CURRENCY = "CHF";
    HALL_CURRENCY_FRACTION_DIGITS = 2;
    HALL_LOCALE = "de-CH";
    HALL_TAX = "8.1";
    HALL_DEFAULT_LANGUAGE = "de";
    HALL_DEFAULT_HALL_ID = 1;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 3;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 0;
    HALL_OFFICIAL_NAME = "Tempodrom Winterthur";
    HALL_RESERVATION_ARRIVAL_MINUTES = 20;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-90898326-2";
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-K499QS6";
    HALL_DEFAULT_BASENAME = "winterthur";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
        {
            id: 2,
            code: "de",
            locale: "de-DE",
            name: "Deutsch",
        },
        // {
        //     id: 3,
        //     code: "it",
        //     locale: "it-IT",
        //     name: "Italiano",
        // },
        // {
        //     id: 3,
        //     code: "nl",
        //     locale: "nl-NL",
        //     name: "Netherlands",
        // },
    ];
    SHOW_PER_PERSON_PRICE = false;
    SHOW_READ_TERMS_BUTTON = false;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = true;
    HALL_HAS_BASENAME = true;
}

if ((DEFAULT_HALL as HallCode) === HallCode.Spreitenbach) {
    HALL_CURRENCY = "CHF";
    HALL_CURRENCY_FRACTION_DIGITS = 2;
    HALL_LOCALE = "de-CH";
    HALL_TAX = "8.1";
    HALL_DEFAULT_LANGUAGE = "de";
    HALL_DEFAULT_HALL_ID = 2;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 3;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 0;
    HALL_OFFICIAL_NAME = "Kartbahn Spreitenbach";
    HALL_RESERVATION_ARRIVAL_MINUTES = 15;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-90898326-3";
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-K499QS6";
    HALL_DEFAULT_BASENAME = "spreitenbach-test";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
        {
            id: 2,
            code: "de",
            locale: "de-DE",
            name: "Deutsch",
        },
        // {
        //     id: 3,
        //     code: "it",
        //     locale: "it-IT",
        //     name: "Italiano",
        // },
    ];
    SHOW_PER_PERSON_PRICE = false;
    SHOW_READ_TERMS_BUTTON = false;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = true;
    HALL_HAS_BASENAME = true;
}

if ((DEFAULT_HALL as HallCode) === HallCode.Budapest) {
    HALL_CURRENCY = "HUF";
    HALL_CURRENCY_FRACTION_DIGITS = 0;
    HALL_LOCALE = "hu-HU";
    HALL_TAX = "27";
    HALL_DEFAULT_LANGUAGE = "hu";
    HALL_DEFAULT_HALL_ID = 4;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 5;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 3;
    HALL_OFFICIAL_NAME = "FlashKart Budapest";
    HALL_RESERVATION_ARRIVAL_MINUTES = 15;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-2773318-72"; //not changed
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-NQJNVGG"; //not changed
    HALL_DEFAULT_BASENAME = "blaha";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "hu",
            locale: "hu-HU",
            name: "Magyar",
        },
        {
            id: 2,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
    ];
    SHOW_PER_PERSON_PRICE = false;
    SHOW_READ_TERMS_BUTTON = false;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = false;
    HALL_HAS_BASENAME = false;
}

if ((DEFAULT_HALL as HallCode) === HallCode.BudapestKoki) {
    HALL_CURRENCY = "HUF";
    HALL_CURRENCY_FRACTION_DIGITS = 0;
    HALL_LOCALE = "hu-HU";
    HALL_TAX = "27";
    HALL_DEFAULT_LANGUAGE = "hu";
    HALL_DEFAULT_HALL_ID = 7;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 5;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 3;
    HALL_OFFICIAL_NAME = "FlashKart Budapest";
    HALL_RESERVATION_ARRIVAL_MINUTES = 15;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-2773318-72"; //not changed
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-NQJNVGG"; //not changed
    HALL_DEFAULT_BASENAME = "koki";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
        {
            id: 2,
            code: "hu",
            locale: "hu-HU",
            name: "Magyar",
        },
    ];
    SHOW_PER_PERSON_PRICE = false;
    SHOW_READ_TERMS_BUTTON = false;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = true;
    HALL_HAS_BASENAME = true;
}

if ((DEFAULT_HALL as HallCode) === HallCode.Trencin) {
    HALL_CURRENCY = "EUR";
    HALL_CURRENCY_FRACTION_DIGITS = 2;
    HALL_LOCALE = "sk-SK";
    HALL_TAX = "20";
    HALL_DEFAULT_LANGUAGE = "sk";
    HALL_DEFAULT_HALL_ID = 3;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 5;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 5;
    HALL_OFFICIAL_NAME = "Volt Racing Trenčín";
    HALL_RESERVATION_ARRIVAL_MINUTES = 15;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-2773318-72"; //not changed
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-NQJNVGG"; //not changed
    HALL_DEFAULT_BASENAME = "trencin";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
        {
            id: 2,
            code: "sk",
            locale: "sk-SK",
            name: "Slovensky",
        },
    ];
    SHOW_PER_PERSON_PRICE = false;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = true;
    HALL_HAS_BASENAME = true;
}

if ((DEFAULT_HALL as HallCode) === HallCode.Zwolle) {
    HALL_CURRENCY = "EUR";
    HALL_CURRENCY_FRACTION_DIGITS = 2;
    HALL_LOCALE = "nl-NL";
    HALL_TAX = "9";
    HALL_DEFAULT_LANGUAGE = "nl";
    HALL_DEFAULT_HALL_ID = 5;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 7;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 0;
    HALL_OFFICIAL_NAME = "Kobalt Zwolle";
    HALL_RESERVATION_ARRIVAL_MINUTES = 15;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-2773318-72";
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-NQJNVGG";
    HALL_DEFAULT_BASENAME = "kobalt";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
        {
            id: 2,
            code: "de",
            locale: "de-DE",
            name: "Deutsch",
        },
        {
            id: 3,
            code: "nl",
            locale: "nl-NL",
            name: "Netherlands",
        },
    ];
    SHOW_PER_PERSON_PRICE = true;
    SHOW_READ_TERMS_BUTTON = true;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = false;
    HALL_HAS_BASENAME = false;
}

if ((DEFAULT_HALL as HallCode) === HallCode.Phpcode) {
    HALL_CURRENCY = "EUR";
    HALL_CURRENCY_FRACTION_DIGITS = 2;
    HALL_LOCALE = "nl-NL";
    HALL_TAX = "9";
    HALL_DEFAULT_LANGUAGE = "nl";
    HALL_DEFAULT_HALL_ID = 420;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 7;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 0;
    HALL_OFFICIAL_NAME = "PHPCode";
    HALL_RESERVATION_ARRIVAL_MINUTES = 15;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-2773318-72"; //not changed
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-NQJNVGG"; //not changed
    HALL_DEFAULT_BASENAME = "phpcode";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
        {
            id: 2,
            code: "de",
            locale: "de-DE",
            name: "Deutsch",
        },
    ];
    SHOW_PER_PERSON_PRICE = true;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = true;
    HALL_HAS_BASENAME = true;
}

if ((DEFAULT_HALL as HallCode) === HallCode.Dev) {
    HALL_CURRENCY = "EUR";
    HALL_CURRENCY_FRACTION_DIGITS = 2;
    HALL_LOCALE = "nl-NL";
    HALL_TAX = "9";
    HALL_DEFAULT_LANGUAGE = "nl";
    HALL_DEFAULT_HALL_ID = 420;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 7;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 0;
    HALL_OFFICIAL_NAME = "Dev bomboclaat";
    HALL_RESERVATION_ARRIVAL_MINUTES = 15;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-2773318-72"; //not changed
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-NQJNVGG"; //not changed
    HALL_DEFAULT_BASENAME = "devbomboclaat";
    SHOW_PER_PERSON_PRICE = true;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = true;
    HALL_HAS_BASENAME = true;
}

if ((DEFAULT_HALL as HallCode) === HallCode.Corner) {
    HALL_CURRENCY = "EUR";
    HALL_CURRENCY_FRACTION_DIGITS = 2;
    HALL_LOCALE = "nl-NL";
    HALL_TAX = "9";
    HALL_DEFAULT_LANGUAGE = "nl";
    HALL_DEFAULT_HALL_ID = 422;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 7;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 0;
    HALL_OFFICIAL_NAME = "Corner";
    HALL_RESERVATION_ARRIVAL_MINUTES = 15;
    HALL_GOOGLE_ANALYTICS_KEY = "UA-2773318-72"; //not changed
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-NQJNVGG"; //not changed
    HALL_DEFAULT_BASENAME = "corner";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
        {
            id: 2,
            code: "de",
            locale: "de-DE",
            name: "Deutsch",
        },
    ];
    SHOW_PER_PERSON_PRICE = true;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = true;
    HALL_HAS_BASENAME = true;
}

if ((DEFAULT_HALL as HallCode) === HallCode.ArboPark) {
    HALL_CURRENCY = "CHF";
    HALL_LOCALE = "de-CH";
    HALL_CURRENCY_FRACTION_DIGITS = 2;
    HALL_TAX = 8.1;
    HALL_DEFAULT_LANGUAGE = "de";
    HALL_DEFAULT_HALL_ID = 1;
    HALL_DEFAULT_RESERVATION_DAYS_LIMIT = 0;
    HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW = 0;
    HALL_OFFICIAL_NAME = "ArboPark";
    HALL_RESERVATION_ARRIVAL_MINUTES = 10;
    HALL_GOOGLE_ANALYTICS_KEY = "G-6L25F5KP4H";
    HALL_GOOGLE_TAG_MANAGER_ID = "GTM-M36GCVWB";
    HALL_DEFAULT_BASENAME = "arbopark";
    HALL_LANGUAGES = [
        {
            id: 1,
            code: "en",
            locale: "en-GB",
            name: "English",
        },
        {
            id: 2,
            code: "de",
            locale: "de-DE",
            name: "Deutsch",
        },
    ];
    SHOW_PER_PERSON_PRICE = false;
    ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = false;
    HALL_IS_ONLY_BOOKING = true;
    HALL_HAS_BASENAME = false;
}

/* apply hall based variable values */
export const CURRENCY = HALL_CURRENCY;
export const CURRENCY_FRACTION_DIGITS = HALL_CURRENCY_FRACTION_DIGITS;
export const LOCALE = HALL_LOCALE;
export const TAX = HALL_TAX;
export const DEFAULT_LANGUAGE = HALL_DEFAULT_LANGUAGE;
export const DEFAULT_RESERVATION_DAYS_LIMIT = HALL_DEFAULT_RESERVATION_DAYS_LIMIT;
export const DEFAULT_HALL_ID = IS_DEV ? HALL_DEFAULT_HALL_ID : HALL_DEFAULT_HALL_ID;
export const DEFAULT_OFFICIAL_NAME = HALL_OFFICIAL_NAME;
export const DEFAULT_RESERVATION_ARRIVAL_MINUTES = HALL_RESERVATION_ARRIVAL_MINUTES;
export const GOOGLE_ANALYTICS_KEY = HALL_GOOGLE_ANALYTICS_KEY;
export const GOOGLE_TAG_MANAGER_ID = HALL_GOOGLE_TAG_MANAGER_ID;
export const DEFAULT_BASENAME = HALL_DEFAULT_BASENAME;
export const DEFAULT_RESERVATION_DAYS_FROM_NOW = HALL_DEFAULT_RESERVATION_DAYS_FROM_NOW;
export const DEFAULT_SHOW_PER_PERSON_PRICE = SHOW_PER_PERSON_PRICE;
export const DEFAULT_SHOW_READ_TERMS_BUTTON = SHOW_READ_TERMS_BUTTON;
export const DEFAULT_ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT = ENABLE_ADJACENT_TIMESLOT_HIGHLIGHT;
export const ANIMATIONS_ENABLED = IS_PROD;
export const IS_PAYMENT_TEST_MODE_ON = false;
export const PAYMENT_TEST = !IS_PROD && IS_PAYMENT_TEST_MODE_ON;
export const GENERIC_ARTICLE_ID = 3000;
export const IS_UPSELL_ALLOWED = true;
export const TIMER_COUNT = 10 * 1000 * 60;
export const PROJECT_VERSION = process.env.REACT_APP_VERSION;
export const MAXIMUM_POINTS_COUNT = 10;
export const DEFAULT_COINS_COUNT = 1500;
export const IS_ONLY_BOOKING: boolean = HALL_IS_ONLY_BOOKING;
export const HAS_BASENAME: boolean = HALL_HAS_BASENAME;

export const HAS_HELP_ENABLED: boolean = false;
export const FILTER_TABS_ENABLED: boolean = true;
export const languages: Language[] = HALL_LANGUAGES;

export interface Translations {
    en: string;
    de: string;
    nl: string;
    it?: string;
    hu?: string;
    sk?: string;
}

export const eventCategories = [
    PackageCategory.EventS,
    PackageCategory.EventM,
    PackageCategory.EventL,
    PackageCategory.SchoolTrophy,
    PackageCategory.MegaFunSchool,
    PackageCategory.PolterabendNonPlusUltra,
    PackageCategory.PolterabendMegaFun,
    PackageCategory.EventMegaFun,
    PackageCategory.EventNonPlusUltra,
];

export const AVAILABLE_DURATIONS_BASIC = [240, 360, 120, 60];
export const AVAILABLE_DURATIONS_EVENTS = [240, 270, 360, 180, 120, 60];

export const BOOKING_MAXIMUM_DATE: Date = new Date("2026-09-30T21:59:59.999Z");

export const FERIENPASS_MINIMUM_DATE: Date = new Date("2026-10-15T00:00:00.000Z");
export const FERIENPASS_MAXIMUM_DATE: Date = new Date("2026-10-22T23:59:59.999Z");
export const IS_FERIENSPASS_ENABLED: boolean = false;
